// Enums
export enum MonitoringEventName {
  CARE_GOALS_HOME_PAGE_ERROR = 'careGoalsHomePageError',
  CARE_GOALS_HOMEPAGE_FOCUS_TIME = 'careGoalsHomepageFocusTime',
  COMMUNITY_RESOURCES_SEARCH = 'communityResourcesSearch',
  COMPLETED_CARE_GOALS_CLICKED = 'completedCareGoalsClicked',
  CONTACT_BUTTON_CLICK = 'contactButtonClick',
  GOAL_CLICK = 'goalClick',
  HOME_BUTTON_CLICK = 'homeButtonClick',
  LIBRARY_SEARCH_KEYWORD = 'librarySearchKeyword',
  LOGIN = 'login',
  LOGIN_FAILED = 'loginFailed',
  LOGIN_STARTED = 'loginStarted',
  LOGIN_SUCCEEDED = 'loginSucceeded',
  MEASURE_HISTORY_CLICKED = 'measureHistoryClicked',
  PAGE_LOAD = 'pageLoad',
  RECOMMENDED_ACTION_CLICKED = 'recommendedActionClicked',
  RECOMMENDED_ACTION_SURFACED = 'recommendedActionSurfaced',
  // surfaced article in recommended article section
  RECOMMENDED_ARTICLE_SURFACED = 'recommendedArticleSurfaced',
  RESOURCE_ARTICLE_TIME = 'resourceArticleTime',
  // landed on article page
  RESOURCE_ARTICLE_VIEWED = 'resourceArticleViewed',
  SYMPTOM_HISTORY_CLICKED = 'symptomHistoryClicked',
  SYMPTOM_SURVEY_CLICKED = 'symptomSurveyClicked',
  TASKS_DETAILS_FOCUS_TIME = 'tasksDetailsFocusTime',
}

// Types
interface HomeButtonMonitoringEvent {
  event: MonitoringEventName.HOME_BUTTON_CLICK
  data: {
    button: string
  }
}

interface ContactButtonMonitoringEvent {
  event: MonitoringEventName.CONTACT_BUTTON_CLICK
  data: {
    method: string
  }
}

interface RecommendedArticleMonitoringEvent {
  event: MonitoringEventName.RECOMMENDED_ARTICLE_SURFACED
  data: {
    recommendedArticleId: string
    slug: string
    pageName: string
  }
}

interface ResourceArticleMonitoringEvent {
  event: MonitoringEventName.RESOURCE_ARTICLE_VIEWED
  data: {
    recommendedArticleId: string | null
    slug: string
  }
}

interface ResourceArticleTimeMonitoringEvent {
  event: MonitoringEventName.RESOURCE_ARTICLE_TIME
  data: {
    recommendedArticleId: string | null
    slug: string
    timeElapsedSeconds: number
  }
}

interface LoginMonitoringEvent {
  event: MonitoringEventName.LOGIN
  data: {
    target: string
  }
}

interface LoginStartedMonitoringEvent {
  event: MonitoringEventName.LOGIN_STARTED
  data: {
    method: string
    timestamp: number
    targetUrl: string
    attemptId: string
    location: string
  }
}

interface LoginSucceededMonitoringEvent {
  event: MonitoringEventName.LOGIN_SUCCEEDED
  data: {
    method: string
    targetUrl: string
    attemptId: string
    durationMs: number
    attemptedMethods: string[]
  }
}

interface LoginFailedMonitoringEvent {
  event: MonitoringEventName.LOGIN_FAILED
  data: {
    attemptedMethods: string[]
    elapsedMs: number
  }
}

interface CommunityResourceSearchEvent {
  event: MonitoringEventName.COMMUNITY_RESOURCES_SEARCH
  data: {
    zipCode: string
  }
}

interface LibrarySearchKeywordEvent {
  event: MonitoringEventName.LIBRARY_SEARCH_KEYWORD
  data: {
    keyword: string
  }
}

interface CompletedGoalMonitoringEvent {
  event: MonitoringEventName.COMPLETED_CARE_GOALS_CLICKED
  data: {
    accordionStatus: string | null
  }
}

interface PageLoadMonitoringEvent {
  event: MonitoringEventName.PAGE_LOAD
  data: {
    pageName: string
  }
}

interface CareGoalsHomepageFocusTimeMonitoringEvent {
  event: MonitoringEventName.CARE_GOALS_HOMEPAGE_FOCUS_TIME
  data: {
    timeElapsedSeconds: number
  }
}

interface TaskDetailsFocusTimeMonitoringEvent {
  event: MonitoringEventName.TASKS_DETAILS_FOCUS_TIME
  data: {
    timeElapsedSeconds: number
    goalId: string
  }
}

interface GoalMonitoringEvent {
  event: MonitoringEventName.GOAL_CLICK
  data: {
    goalId: string
    goalTitle: string
  }
}

interface RecommendedActionClickedMonitoringEvent {
  event: MonitoringEventName.RECOMMENDED_ACTION_CLICKED
  data: {
    name: string
    ranking: number
  }
}

interface RecommendedActionSurfacedMonitoringEvent {
  event: MonitoringEventName.RECOMMENDED_ACTION_SURFACED
  data: {
    name: string
  }
}

interface CareGoalsListErrorEvent {
  event: MonitoringEventName.CARE_GOALS_HOME_PAGE_ERROR
  data: {
    error: Error | null
  }
}

interface SymptomSurveyClickedMonitoringEvent {
  event: MonitoringEventName.SYMPTOM_SURVEY_CLICKED
  data: {
    pageName: string
  }
}

interface SymptomHistoryClickedMonitoringEvent {
  event: MonitoringEventName.SYMPTOM_HISTORY_CLICKED
  data: {
    symptom: string
  }
}

interface MeasureHistoryClickedMonitoringEvent {
  event: MonitoringEventName.MEASURE_HISTORY_CLICKED
  data: {
    symptom: string
    measure: string
  }
}

// Combined type
export type MonitoringEvent =
  | HomeButtonMonitoringEvent
  | ContactButtonMonitoringEvent
  | RecommendedArticleMonitoringEvent
  | ResourceArticleMonitoringEvent
  | ResourceArticleTimeMonitoringEvent
  | LoginMonitoringEvent
  | LoginStartedMonitoringEvent
  | LoginSucceededMonitoringEvent
  | LoginFailedMonitoringEvent
  | CommunityResourceSearchEvent
  | LibrarySearchKeywordEvent
  | GoalMonitoringEvent
  | CompletedGoalMonitoringEvent
  | CareGoalsHomepageFocusTimeMonitoringEvent
  | TaskDetailsFocusTimeMonitoringEvent
  | PageLoadMonitoringEvent
  | RecommendedActionClickedMonitoringEvent
  | RecommendedActionSurfacedMonitoringEvent
  | CareGoalsListErrorEvent
  | SymptomSurveyClickedMonitoringEvent
  | SymptomHistoryClickedMonitoringEvent
  | MeasureHistoryClickedMonitoringEvent

export type MonitoringEventData<T extends MonitoringEventName> = Extract<
  MonitoringEvent,
  { event: T }
>['data']
